import { Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import { Info } from "@material-ui/icons";
import Checkbox from "@saleor/components/Checkbox";
import Money from "@saleor/components/Money";
import Percent from "@saleor/components/Percent";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { BulkVoucherTabList_voucherGroups_edges_node } from "@saleor/discounts/types/BulkVoucherTabList";
import { VoucherListUrlSortField } from "@saleor/discounts/urls";
import { maybe, renderCollection } from "@saleor/misc";
import { ListActions, ListProps, SortPage } from "@saleor/types";
import {
  DiscountValueTypeEnum,
  VoucherDiscountType,
  VoucherTypeEnum
} from "@saleor/types/globalTypes";
import { getArrowDirection } from "@saleor/utils/sort";
import { getFooterColSpanWithBulkActions } from "@saleor/utils/tables";
import React from "react";
import { FormattedMessage } from "react-intl";
import Moment from "react-moment";

import { VoucherList_vouchers_edges_node } from "../../types/VoucherList";

export interface VoucherListProps
  extends ListProps,
    ListActions,
    SortPage<VoucherListUrlSortField> {
  defaultCurrency: string;
  vouchers: VoucherList_vouchers_edges_node[];
  voucherGroups: BulkVoucherTabList_voucherGroups_edges_node[];
  onArrowClick: (id?: string) => () => void;
  activeTab: string;
}

const useStyles = makeStyles(
  theme => ({
    [theme.breakpoints.up("lg")]: {
      colEnd: {
        width: 180
      },
      colMinSpent: {
        width: 180
      },
      colName: {},
      colStart: {
        width: 200
      },
      colUses: {
        width: 250
      },
      colValue: {
        width: 180
      }
    },
    arrow: {
      color: "#247CFF"
    },
    btnArrow: {
      color: "#247CFF",
      cursor: "pointer",
      paddingLeft: 10
    },
    colEnd: {
      textAlign: "right"
    },
    colMinSpent: {
      textAlign: "right"
    },
    colName: {
      overflow: "hidden",
      paddingLeft: 0,
      textOverflow: "ellipsis",
      width: 220
    },
    colStart: {
      textAlign: "right"
    },
    colType: {
      textAlign: "left",
      width: 200
    },
    colUses: {
      textAlign: "left"
    },
    colValue: {
      textAlign: "right"
    },
    tableCode: {
      backgroundColor: "#96a7f7",
      borderRadius: "5px",
      color: "#1440ff",
      fontWeight: "bold",
      padding: "6px",
      textAlign: "center",
      width: "auto !important"
    },
    tableCodes: {
      backgroundColor: "#fef7b4",
      borderRadius: "5px",
      color: "#edad32",
      fontWeight: "bold",
      padding: "6px",
      textAlign: "center"
    },
    tableRow: {
      cursor: "pointer"
    },
    textRight: {
      textAlign: "right"
    }
  }),
  { name: "VoucherList" }
);

const numberOfColumns = 7;
const tableHeadNumCol = 8;

const VoucherList: React.FC<VoucherListProps> = props => {
  const {
    settings,
    defaultCurrency,
    disabled,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    onRowClick,
    onSort,
    pageInfo,
    vouchers,
    isChecked,
    selected,
    sort,
    toggle,
    toggleAll,
    toolbar
  } = props;
  const classes = useStyles(props);
  function getVoucherType(data) {
    let value = "";
    const type = data?.maxDiscountValue
      ? VoucherDiscountType.PERCENTAGE_AND_CAPPED
      : data?.type === VoucherTypeEnum.SHIPPING
      ? VoucherTypeEnum.SHIPPING
      : data?.type === VoucherTypeEnum.E_VOUCHER
      ? VoucherTypeEnum.E_VOUCHER
      : data.discountValueType;

    switch (type) {
      case VoucherDiscountType.PERCENTAGE_AND_CAPPED:
        value = "CAPPED AND PERCENTAGE";
        break;
      case VoucherTypeEnum.SHIPPING:
        value = "FREE SHIPPING";
        break;
      case VoucherTypeEnum.E_VOUCHER:
        value = "E-VOUCHER";
        break;
      case "FIXED":
        value = "FIXED AMOUNT";
        break;
      default:
        value = "PERCENTAGE";
        break;
    }
    return value;
  }

  return (
    <ResponsiveTable>
      <TableHead
        colSpan={tableHeadNumCol}
        selected={selected}
        disabled={disabled}
        items={vouchers}
        toggleAll={toggleAll}
        toolbar={toolbar}
      >
        <TableCellHeader
          direction={
            sort.sort === VoucherListUrlSortField.code
              ? getArrowDirection(sort.asc)
              : undefined
          }
          arrowPosition="right"
          onClick={() => onSort(VoucherListUrlSortField.code)}
          className={classes.colName}
        >
          <FormattedMessage
            defaultMessage="Voucher Code"
            description="voucher code"
          />
        </TableCellHeader>
        <TableCellHeader textAlign="left" className={classes.colType}>
          <FormattedMessage
            defaultMessage="Discount Type"
            description="voucher type"
          />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === VoucherListUrlSortField.minSpent
              ? getArrowDirection(sort.asc)
              : undefined
          }
          textAlign="right"
          onClick={() => onSort(VoucherListUrlSortField.minSpent)}
          className={classes.colMinSpent}
        >
          <FormattedMessage
            defaultMessage="Minimum Spent"
            description="minimum amount of spent money to activate voucher"
          />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === VoucherListUrlSortField.startDate
              ? getArrowDirection(sort.asc)
              : undefined
          }
          textAlign="right"
          onClick={() => onSort(VoucherListUrlSortField.startDate)}
          className={classes.colStart}
        >
          <FormattedMessage
            defaultMessage="Start Date"
            description="voucher is active from date"
          />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === VoucherListUrlSortField.endDate
              ? getArrowDirection(sort.asc)
              : undefined
          }
          textAlign="right"
          onClick={() => onSort(VoucherListUrlSortField.endDate)}
          className={classes.colEnd}
        >
          <FormattedMessage
            defaultMessage="End Date"
            description="voucher is active until date"
          />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === VoucherListUrlSortField.value
              ? getArrowDirection(sort.asc)
              : undefined
          }
          textAlign="right"
          onClick={() => onSort(VoucherListUrlSortField.value)}
          className={classes.colValue}
        >
          <FormattedMessage
            defaultMessage="Discount Value"
            description="voucher value"
          />
        </TableCellHeader>
        <TableCellHeader textAlign="left" className={classes.colUses}>
          <FormattedMessage
            defaultMessage="Voucher Status"
            description="voucher status"
          />
        </TableCellHeader>
      </TableHead>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={getFooterColSpanWithBulkActions(vouchers, numberOfColumns)}
            settings={settings}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            onUpdateListSettings={onUpdateListSettings}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>
      <TableBody>
        {renderCollection(
          vouchers,
          (voucher, index) => {
            const isSelected = voucher ? isChecked(voucher.id) : false;
            return (
              <TableRow
                className={!!voucher ? classes.tableRow : undefined}
                hover={!!voucher}
                key={voucher ? voucher.id : "skeleton"}
                selected={isSelected}
                onClick={voucher ? onRowClick(voucher.id) : undefined}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected}
                    disabled={disabled}
                    disableClickPropagation
                    onChange={() => toggle(voucher.id)}
                    id={`singleVoucherCheckboxRow${index}`}
                  />
                </TableCell>
                <TableCell
                  className={classes.colName}
                  id={`singleVoucherVoucherCodeRow${index}`}
                >
                  <div className={classes.tableCode}>
                    {maybe<React.ReactNode>(() => voucher.code, <Skeleton />)}
                    {maybe<React.ReactNode>(() =>
                      voucher.isHidden ? (
                        <Tooltip title="Hidden" aria-label="add">
                          <Info fontSize="small" />
                        </Tooltip>
                      ) : (
                        ""
                      )
                    )}
                  </div>
                </TableCell>
                <TableCell
                  className={classes.colType}
                  id={`singleVoucherDiscountTypeRow${index}`}
                >
                  {maybe<React.ReactNode>(
                    () => getVoucherType(voucher),
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colMinSpent}
                  id={`singleVoucherMinSpentRow${index}`}
                >
                  {voucher && voucher.minSpent ? (
                    <Money money={voucher.minSpent} />
                  ) : voucher && voucher.minSpent === null ? (
                    "-"
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colStart}
                  id={`singleVoucherStartDateRow${index}`}
                >
                  {voucher && voucher.startDate ? (
                    <Moment format="MM/DD/YYYY" tz="Asia/Manila">
                      {voucher?.startDate}
                    </Moment>
                  ) : (
                    // <Date date={voucher.startDate} />
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colEnd}
                  id={`singleVoucherEndDateRow${index}`}
                >
                  {voucher && voucher.endDate ? (
                    <Moment format="MM/DD/YYYY" tz="Asia/Manila">
                      {voucher?.endDate}
                    </Moment>
                  ) : // <Date date={voucher.endDate} />
                  voucher && voucher.endDate === null ? (
                    "-"
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colValue}
                  onClick={voucher ? onRowClick(voucher.id) : undefined}
                  id={`singleVoucherDiscountValueRow${index}`}
                >
                  {voucher &&
                  voucher.discountValueType &&
                  voucher.discountValue ? (
                    voucher.discountValueType ===
                    DiscountValueTypeEnum.FIXED ? (
                      <Money
                        money={{
                          amount: voucher.discountValue,
                          currency: defaultCurrency
                        }}
                      />
                    ) : (
                      <Percent amount={voucher.discountValue} />
                    )
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colUses}
                  id={`singleVoucherVoucherStatusRow${index}`}
                >
                  {maybe<React.ReactNode>(() => voucher.status, <Skeleton />)}
                </TableCell>
              </TableRow>
            );
          },
          () => (
            <TableRow>
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage defaultMessage="No vouchers found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};
VoucherList.displayName = "VoucherList";
export default VoucherList;
